import { ReactNode, useCallback, useEffect, useState } from 'react';
import { LocalStorageKey } from '@diritto/common/utils/localStorage';
import { useModeGuideFABVisibility } from 'ui-hooks/useModeGuideFABVisibility';
import { useMedia } from 'ui-hooks/useMediaQuery';

interface ModeGuideFABProps {
  children: ReactNode;
}

const BOTTOM_THRESHOLD = 200;

export const ModeGuideFAB = ({ children }: ModeGuideFABProps) => {
  const { showModeGuideFAB } = useModeGuideFABVisibility();
  const { isPc } = useMedia();
  const [opacity, setOpacity] = useState(1);
  const [isDismissedByClick, setIsDismissedByClick] = useState(false);

  useEffect(() => {
    if (!showModeGuideFAB) {
      return () => {};
    }

    const checkScrollPosition = () => {
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const distanceFromBottom = documentHeight - (scrollTop + windowHeight);

      if (distanceFromBottom <= BOTTOM_THRESHOLD) {
        setOpacity(0);
      } else if (!isDismissedByClick) {
        setOpacity(1);
      }
    };

    checkScrollPosition();

    window.addEventListener('scroll', checkScrollPosition, { passive: true });

    return () => {
      window.removeEventListener('scroll', checkScrollPosition);
    };
  }, [showModeGuideFAB, isDismissedByClick]);

  const handleClick = useCallback(() => {
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: 'smooth',
    });

    localStorage.setItem(LocalStorageKey.ModeGuideFABDismissed, 'true');
    setIsDismissedByClick(true);
  }, []);

  if (!showModeGuideFAB) {
    return null;
  }

  return (
    <button
      onClick={handleClick}
      className={[
        'bg-primary-500 fixed left-1/2 -translate-x-1/2 flex items-center gap-1 pl-4 pr-3 py-3 rounded-3xl',
        `z-[10] shadow-[0_4px_10px_rgba(69,44,123,0.25)] border border-solid border-primary-400 transition-opacity duration-300`,
        `cursor-pointer ${opacity === 0 ? 'pointer-events-none' : ''}`,
      ].join(' ')}
      style={{
        opacity,
        bottom: isPc ? `calc(4px + var(--safe-area-bottom,0px))` : `calc(72px + var(--safe-area-bottom,0px))`,
      }}>
      {children}
    </button>
  );
};
